import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { CalendarDays, MapPin, Plane } from "lucide-react";
import { BOLIVIA_MAP } from "@/data/boliviaMap";
import { MAP_PINS, waLink, track } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { useCertero } from "@/context/CerteroContext";

const { viewBox, w, h, lon0, lat1, k, pad, departamentos } = BOLIVIA_MAP;
const proj = (lon, lat) => [(lon - lon0) * k + pad, (lat1 - lat) * k + pad];
const ACTIVOS = ["santa-cruz", "beni", "cochabamba"];
const SEDE = proj(-63.18, -17.78);
const PIN_SEDE = MAP_PINS.find((p) => p.id === "sede");
const PIN_BENI = MAP_PINS.find((p) => p.id === "beni");
const ZONAS_SC = MAP_PINS.filter((p) => ["norte", "este", "sur", "chiquitania", "frontera"].includes(p.id));
const ZOOM = 2.7;

// Texto fijo por departamento (los datos ya están en la web; no se inventan zonas)
const DEP_DATA = {
  "santa-cruz": { nombre: "Santa Cruz" },
  beni: { nombre: "Beni" },
  cochabamba: { nombre: "Cochabamba" },
};

function DronIcon({ x, y, opacity = 1 }) {
  return (
    <g transform={`translate(${x} ${y})`} opacity={opacity} aria-hidden="true">
      <line x1="-7" y1="-5" x2="7" y2="5" stroke="currentColor" strokeWidth="1.4" />
      <line x1="7" y1="-5" x2="-7" y2="5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="-7" cy="-5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="-5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="-7" cy="5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="0" cy="0" r="2.2" fill="currentColor" />
    </g>
  );
}

export default function BoliviaMapa() {
  const ref = useRef(null);
  const rafRef = useRef(0);
  const enVista = useInView(ref, { once: true, amount: 0.25 });
  const [reduce, setReduce] = useState(false);
  const [zoom, setZoom] = useState(null); // null = toda Bolivia; objeto departamento = enfocado
  const [sel, setSel] = useState(null); // { tipo: "dept"|"zona", id, nombre, ... }
  const [vuelo, setVuelo] = useState(null); // { x2, y2, t, listo }
  const [dronePos, setDronePos] = useState(null);

  useEffect(() => setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches), []);

  // Al llegar a la sección: 1,5 s después el mapa se acerca a Santa Cruz y marca la sede
  useEffect(() => {
    if (!enVista) return;
    const t = setTimeout(() => {
      const sc = departamentos.find((d) => d.id === "santa-cruz");
      setZoom(reduce ? null : sc);
      setSel({ tipo: "dept", id: "santa-cruz", nombre: "Santa Cruz" });
    }, reduce ? 0 : 1500);
    return () => clearTimeout(t);
  }, [enVista, reduce, departamentos]);

  const cancelarVuelo = () => { cancelAnimationFrame(rafRef.current); };

  // Vuelo del dron: sede → centro del departamento, 1,8 s, línea punteada
  const volar = (destino) => {
    cancelarVuelo();
    if (reduce) return;
    const dur = 1800;
    const t0 = performance.now();
    setVuelo({ x2: destino[0], y2: destino[1], listo: false });
    setDronePos(SEDE);
    const paso = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; // easeInOutQuad
      setDronePos([SEDE[0] + (destino[0] - SEDE[0]) * e, SEDE[1] + (destino[1] - SEDE[1]) * e]);
      if (p < 1) rafRef.current = requestAnimationFrame(paso);
      else setVuelo((v) => (v ? { ...v, listo: true } : v));
    };
    rafRef.current = requestAnimationFrame(paso);
  };

  useEffect(() => cancelarVuelo, []);

  const irDept = (d, { volar: conVuelo = true, trackear = true } = {}) => {
    if (trackear) track("mapa_departamento_click", { departamento: d.nombre });
    setSel({ tipo: "dept", id: d.id, nombre: d.nombre });
    setZoom(reduce ? null : d);
    if (conVuelo) volar([d.cx, d.cy]);
    else cancelarVuelo();
  };

  const irZona = (pin) => {
    track("mapa_zona_click", { zona: pin.nombre });
    setSel({ tipo: "zona", id: pin.id, nombre: pin.nombre, pin });
  };

  const verTodaBolivia = () => {
    track("mapa_toda_bolivia_click", {});
    cancelarVuelo();
    setDronePos(null);
    setVuelo(null);
    setZoom(null);
    setSel(null);
  };

  // Puntos de zona visibles: al estar sobre Santa Cruz (o sus zonas)
  const mostrarZonasSC = sel && (sel.id === "santa-cruz" || ZONAS_SC.some((z) => z.id === sel.id));
  const mostrarBeni = sel && (sel.id === "beni" || sel.id === "zona-beni");

  const card = sel
    ? sel.tipo === "zona"
      ? sel.pin
      : { nombre: DEP_DATA[sel.id]?.nombre || sel.nombre, esDept: true }
    : { ...PIN_SEDE, esSede: true };

  const msgWhatsApp = sel?.tipo === "zona"
    ? `Hola CERTERO SRL, quiero reservar una demostración en campo en la zona: ${sel.pin.nombre}.`
    : `Hola CERTERO SRL, quiero reservar una demostración en campo en el departamento de ${card.nombre}.`;

  const zonasEnTarjeta = useMemo(() => (sel?.tipo === "zona" ? ZONAS_SC.find((z) => z.id === sel.id) : null), [sel]);

  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
      {/* Mapa + chips */}
      <Reveal className="lg:col-span-7">
        <div ref={ref} className="relative border border-hud bg-ink p-4 sm:p-8" data-testid="demo-map">
          <svg
            viewBox={viewBox}
            className="mx-auto h-auto w-full max-w-[520px] select-none"
            role="img"
            aria-label="Mapa real de Bolivia por departamentos con la cobertura de CERTERO"
          >
            <g
              style={{
                transform: zoom ? `translate(${w / 2 - ZOOM * zoom.cx}px, ${h / 2 - ZOOM * zoom.cy}px) scale(${ZOOM})` : "none",
                transition: reduce ? "none" : "transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {departamentos.map((d) => {
                const activo = ACTIVOS.includes(d.id);
                const encendido = sel && (sel.id === d.id || (sel.tipo === "zona" && sel.pin && sel.pin.zona.includes(d.nombre.split(" ")[0]) && d.id === "santa-cruz"));
                return (
                  <path
                    key={d.id}
                    data-testid={`demo-map-dep-${d.id}`}
                    d={d.d}
                    role={activo ? "button" : undefined}
                    tabIndex={activo ? 0 : undefined}
                    aria-label={activo ? `Departamento de ${d.nombre}` : undefined}
                    onClick={activo ? () => irDept(d) : undefined}
                    onKeyDown={activo ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); irDept(d); } } : undefined}
                    className={`transition-[fill,stroke] duration-500 ${activo ? "cursor-pointer focus:outline-none focus-visible:stroke-certero" : ""}`}
                    fill={encendido ? "rgba(76,194,67,0.30)" : activo ? "rgba(19,58,110,0.55)" : "rgba(12,41,92,0.22)"}
                    stroke={encendido ? "rgba(82,194,52,0.95)" : activo ? "rgba(59,170,52,0.55)" : "rgba(38,80,140,0.4)"}
                    strokeWidth={encendido ? 1.6 : 1.1}
                  />
                );
              })}

              {/* Ruta punteada del vuelo */}
              {vuelo && dronePos && (
                <g className="text-certero" data-testid="mapa-vuelo">
                  <line
                    x1={SEDE[0]} y1={SEDE[1]} x2={vuelo.x2} y2={vuelo.y2}
                    stroke="rgba(82,194,52,0.75)" strokeWidth="1.4" strokeDasharray="3 7" strokeLinecap="round"
                  />
                  <DronIcon x={dronePos[0]} y={dronePos[1]} opacity={vuelo.listo ? 0.35 : 1} />
                </g>
              )}

              {/* Sede (siempre) */}
              <g transform={`translate(${SEDE[0]} ${SEDE[1]})`}>
                {enVista && <circle r="6" fill="rgba(82,194,52,0.45)" className="motion-safe:animate-ping" />}
                <circle r="4" fill="#52c234" stroke="#060b12" strokeWidth="1.4" data-testid="mapa-sede" />
              </g>

              {/* Zonas de Santa Cruz */}
              {mostrarZonasSC && ZONAS_SC.map((p) => {
                const [x, y] = proj(p.lon, p.lat);
                const on = sel?.tipo === "zona" && sel.id === p.id;
                return (
                  <g
                    key={p.id}
                    transform={`translate(${x} ${y})`}
                    className="cursor-pointer"
                    role="button"
                    tabIndex={0}
                    data-testid={`demo-map-pin-${p.id}`}
                    aria-label={`Zona: ${p.nombre}`}
                    onClick={(e) => { e.stopPropagation(); irZona(p); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); irZona(p); } }}
                  >
                    <circle r="12" fill="transparent" />
                    <circle r={on ? 5.2 : 3.4} fill={on ? "#52c234" : "#060b12"} stroke="#52c234" strokeWidth="1.6" className="transition-all duration-300" />
                    <text y={-10} textAnchor="middle" className="fill-mist/80 font-mono" style={{ fontSize: 8.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      {p.nombre}
                    </text>
                  </g>
                );
              })}

              {/* Punto del Beni */}
              {mostrarBeni && PIN_BENI && (() => {
                const [x, y] = proj(PIN_BENI.lon, PIN_BENI.lat);
                const on = sel?.id === "beni";
                return (
                  <g key="pin-beni" transform={`translate(${x} ${y})`} className="cursor-pointer" role="button" tabIndex={0}
                    data-testid="demo-map-pin-beni-dep"
                    aria-label="Zona: Departamento del Beni"
                    onClick={(e) => { e.stopPropagation(); setSel({ tipo: "dept", id: "beni", nombre: "Beni" }); }}
                  >
                    <circle r="12" fill="transparent" />
                    <circle r={on ? 5.2 : 3.4} fill={on ? "#52c234" : "#060b12"} stroke="#52c234" strokeWidth="1.6" className="transition-all duration-300" />
                  </g>
                );
              })()}
            </g>
          </svg>

          <div className="absolute bottom-3 left-3 flex flex-wrap gap-4 font-mono text-[9px] uppercase tracking-[0.2em] text-smoke/80">
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-certero" /> Sede — Santa Cruz de la Sierra</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full border border-certero/70" /> Zonas de cobertura</span>
          </div>
        </div>

        {/* Botones de departamentos (debajo del mapa) */}
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="demo-map-chips">
          {departamentos.filter((d) => ACTIVOS.includes(d.id)).map((d) => (
            <button
              key={d.id}
              data-testid={`demo-map-btn-${d.id}`}
              onClick={() => irDept(d)}
              aria-pressed={sel?.tipo === "dept" && sel.id === d.id}
              className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                sel?.tipo === "dept" && sel.id === d.id
                  ? "bg-certero text-ink"
                  : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
              }`}
            >
              {d.nombre}
            </button>
          ))}
          <button
            data-testid="demo-map-btn-toda-bolivia"
            onClick={verTodaBolivia}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
              !zoom && !sel
                ? "bg-certero text-ink"
                : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
            }`}
          >
            <GlobeIcon /> Ver toda Bolivia
          </button>
        </div>
      </Reveal>

      {/* Tarjeta de detalle */}
      <Reveal delay={0.12} className="lg:col-span-5">
        <div data-testid="demo-card-detail" className="flex h-full flex-col border border-hud bg-surface">
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <span className="self-start border border-certero/60 bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-certero">
              {sel?.tipo === "zona" ? sel.pin.tipo : card.esSede ? PIN_SEDE.tipo : "PRESENCIA CERTERO"}
            </span>
            <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl" data-testid="demo-card-titulo">
              {card.nombre}
            </h3>
            <p className="mt-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-certero" /> {card.esDept ? `Departamento de ${card.nombre} — Bolivia` : card.zona}
            </p>

            {sel?.tipo === "zona" ? (
              <>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke sm:text-base">{sel.pin.desc}</p>
                <p className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist/70">
                  <CalendarDays className="h-3.5 w-3.5 text-certero" /> {sel.pin.fecha}
                </p>
              </>
            ) : card.esSede ? (
              <>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke sm:text-base">{PIN_SEDE.desc}</p>
                <p className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist/70">
                  <CalendarDays className="h-3.5 w-3.5 text-certero" /> {PIN_SEDE.fecha}
                </p>
              </>
            ) : (
              <>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke sm:text-base" data-testid="demo-card-campana">
                  Campaña de verano · octubre a junio.
                </p>
                <p className="text-sm leading-relaxed text-smoke sm:text-base">
                  Servicios agrícolas y entrega de drones con capacitación.
                </p>
              </>
            )}

            <a
              data-testid="demo-btn-reservar"
              href={waLink(msgWhatsApp)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("reservar_demo_whatsapp", { zona: card.nombre })}
              className="mt-7 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
            >
              <Plane className="h-4 w-4" />
              {sel?.tipo === "zona" ? `Reservar demo en ${sel.pin.nombre}` : card.esSede ? "Reservar demo en campo" : `Reservar demo en ${card.nombre}`}
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
    </svg>
  );
}
